import { memo, useCallback, useMemo } from 'react'

import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import { ContactStatus, LoadingState } from 'clickNgo/entities/contact'
import {
  ContactFlagRequestAction,
  emptyContactFlags,
  IContactFlag,
} from 'clickNgo/entities/contactFlag'
import { useUpdateContactFlagsMutation } from 'clickNgo/entities/contactFlag/api/contactFlagApi'
import { ContactFlags } from 'clickNgo/entities/contactFlag/ui'
import { useGetFormDataField } from 'cng-common/features/form/index'

import { ContactEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks'
import { ContactEditorSettingFormFields } from '../../../models'

import { ALLOWED_PORTAL_ACCESS } from './configs'
import { IWrappedContactFlagsProps } from './models'

export const WrappedContactFlags = memo<IWrappedContactFlagsProps>(
  ({ type, onContactFlagsUpdate, contactFlags = emptyContactFlags, contact }) => {
    const { isReadOnly } = useReadOnlyMenuItem()
    const [, setIsLoading] = useGetFormDataField<LoadingState>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.IsLoading
    )

    const [updateContactFlags] = useUpdateContactFlagsMutation()

    const isInactiveContact = contact.status === ContactStatus.Inactive
    const { market, region } = contact

    const hasPortalAccess = useMemo(() => {
      return contactFlags.UserRights.some((flag) => ALLOWED_PORTAL_ACCESS.includes(flag.code))
    }, [contactFlags])

    const handleOnContactFlagsAdd = useCallback(
      async (newContactFlags: IContactFlag[]) => {
        setIsLoading({ isLoading: true, message: 'Adding contact flags...' })
        try {
          await updateContactFlags({
            contactFlags: newContactFlags,
            action: ContactFlagRequestAction.Add,
            source: ProfilingRequestSource.ClickAndGoPortal,
          })
          const newFlags = [...contactFlags[type], ...newContactFlags]

          await onContactFlagsUpdate({
            ...contactFlags,
            [type]: newFlags,
          })
        } finally {
          setIsLoading({ isLoading: false, message: '' })
        }
      },
      [contactFlags, onContactFlagsUpdate, setIsLoading, type, updateContactFlags]
    )

    const handleOnContactFlagsDelete = useCallback(
      async (deletedFlags: IContactFlag[]) => {
        setIsLoading({ isLoading: true, message: 'Removing contact flags...' })
        try {
          await updateContactFlags({
            contactFlags: deletedFlags,
            action: ContactFlagRequestAction.Delete,
            source: ProfilingRequestSource.ClickAndGoPortal,
          })
          const newFlags = contactFlags[type].filter((flag) =>
            deletedFlags.every((req) => req.code !== flag.code)
          )
          await onContactFlagsUpdate({
            ...contactFlags,
            [type]: newFlags,
          })
        } finally {
          setIsLoading({ isLoading: false, message: '' })
        }
      },
      [contactFlags, onContactFlagsUpdate, setIsLoading, type, updateContactFlags]
    )

    return (
      <ContactFlags
        hasPortalAccess={hasPortalAccess}
        contact={contact}
        contactFlags={contactFlags[type]}
        market={market}
        region={region}
        type={type}
        onAdd={handleOnContactFlagsAdd}
        onDelete={handleOnContactFlagsDelete}
        isReadOnly={isReadOnly || isInactiveContact}
      />
    )
  }
)

WrappedContactFlags.displayName = 'WrappedContactFlags'
